import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import { AttachFile, Download } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { formatDateWithTimezone } from '../../utils/timezoneUtils';

const EXTENSION_UNITS = ['Days', 'Months', 'Years', 'Hours', 'Cycles', 'Landings', 'Calendar Days'];

const statusColor = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'PENDING_AM_APPROVAL':
      return 'warning';
    case 'REJECTED':
    case 'REVOKED':
    case 'EXPIRED':
      return 'default';
    default:
      return 'info';
  }
};

const ExtendDueDialog = ({
  open,
  onClose,
  item,
  onSuccess,
  canRequestDueExtension,
  canApproveDueExtension,
  canRevokeDueExtension,
  displayTimezone
}) => {
  const { showToast } = useToast();
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extensionValue, setExtensionValue] = useState('');
  const [extensionUnit, setExtensionUnit] = useState('Days');
  const [remarks, setRemarks] = useState('');
  const [file, setFile] = useState(null);
  const [rejectionRemarks, setRejectionRemarks] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [error, setError] = useState('');

  const sourceKey = item
    ? [
        item.source,
        item.sourceId,
        item.tsrIndex != null ? item.tsrIndex : '',
        item.scheduleIndex != null ? item.scheduleIndex : '',
        item.aircraftId || ''
      ].join('|')
    : '';

  const loadExtensions = useCallback(async () => {
    if (!sourceKey) return;
    try {
      const res = await axios.get('/api/maintenance/due-extensions/active', {
        params: { sourceKey }
      });
      setExtensions(Array.isArray(res.data) ? res.data : []);
    } catch {
      showToast('Failed to load extensions', 'error');
    }
  }, [sourceKey, showToast]);

  useEffect(() => {
    if (open && item) {
      setExtensionValue('');
      setExtensionUnit('Days');
      setRemarks('');
      setFile(null);
      setRejectionRemarks('');
      setRejectingId(null);
      setError('');
      loadExtensions();
    }
  }, [open, item, loadExtensions]);

  const handleRequest = async () => {
    if (!item) return;
    if (!extensionValue.trim()) {
      setError('Extension value is required');
      return;
    }
    if (!remarks.trim()) {
      setError('Remarks are required');
      return;
    }
    if (!file) {
      setError('DGCA approval document is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const form = new FormData();
      form.append('upcomingItem', JSON.stringify(item));
      form.append('extensionValue', extensionValue.trim());
      form.append('extensionUnit', extensionUnit);
      form.append('remarks', remarks.trim());
      form.append('dgcaApproval', file);
      await axios.post('/api/maintenance/due-extensions', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Extension request submitted for AM approval', 'success');
      setExtensionValue('');
      setRemarks('');
      setFile(null);
      await loadExtensions();
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit extension request';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      setLoading(true);
      await axios.post(`/api/maintenance/due-extensions/${id}/approve`);
      showToast('Extension approved', 'success');
      await loadExtensions();
      if (onSuccess) onSuccess();
    } catch (err) {
      showToast(err.response?.data?.message || 'Approval failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id) => {
    if (!rejectionRemarks.trim()) {
      setError('Rejection remarks are required');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`/api/maintenance/due-extensions/${id}/reject`, {
        rejectionRemarks: rejectionRemarks.trim()
      });
      showToast('Extension rejected', 'success');
      setRejectingId(null);
      setRejectionRemarks('');
      await loadExtensions();
    } catch (err) {
      showToast(err.response?.data?.message || 'Rejection failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      setLoading(true);
      await axios.post(`/api/maintenance/due-extensions/${id}/revoke`, {
        revocationRemarks: 'Revoked by AM'
      });
      showToast('Extension revoked', 'success');
      await loadExtensions();
      if (onSuccess) onSuccess();
    } catch (err) {
      showToast(err.response?.data?.message || 'Revoke failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  const pending = extensions.filter((e) => e.status === 'PENDING_AM_APPROVAL');
  const approved = extensions.filter((e) => e.status === 'APPROVED');
  const history = extensions.filter((e) =>
    ['REJECTED', 'REVOKED', 'EXPIRED', 'CONSUMED'].includes(e.status)
  );

  const baselineDueDisplay = item.baselineDueDate
    ? displayTimezone
      ? formatDateWithTimezone(item.baselineDueDate, displayTimezone)
      : new Date(item.baselineDueDate).toLocaleDateString()
    : item.dueAtLimit
      ? `${item.dueAtLimit.value} ${item.dueAtLimit.unit}`
      : item.dueByRemaining
        ? `${item.dueByRemaining.value} ${item.dueByRemaining.unit} remaining`
        : '—';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Extend Due — {item.title}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Aircraft</Typography>
            <Typography variant="body2">{item.aircraftRegistration || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">TSN / Type</Typography>
            <Typography variant="body2">{item.tsrType || item.type || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Baseline due</Typography>
            <Typography variant="body2">{baselineDueDisplay}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Effective due (with extensions)</Typography>
            <Typography variant="body2" sx={{ fontWeight: item.isExtended ? 'bold' : 'normal' }}>
              {item.dueDate
                ? displayTimezone
                  ? formatDateWithTimezone(item.dueDate, displayTimezone)
                  : new Date(item.dueDate).toLocaleDateString()
                : item.dueByRemaining
                  ? `${item.dueByRemaining.value} ${item.dueByRemaining.unit}`
                  : '—'}
            </Typography>
          </Grid>
        </Grid>

        {item.isExtended && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Active extension(s) apply. Baseline due is suppressed in compliance/audit overdue views until the extended due is reached.
          </Alert>
        )}

        {approved.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Active approved extensions</Typography>
            <List dense disablePadding>
              {approved.map((ext) => (
                <ListItem
                  key={ext._id}
                  secondaryAction={
                    canRevokeDueExtension ? (
                      <Button size="small" color="warning" onClick={() => handleRevoke(ext._id)} disabled={loading}>
                        Revoke
                      </Button>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={`+${ext.extensionAmount?.value} ${ext.extensionAmount?.unit}`}
                    secondary={ext.remarks}
                  />
                  {ext.dgcaApprovalDocument?.fileUrl && (
                    <IconButton
                      size="small"
                      href={ext.dgcaApprovalDocument.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download DGCA approval"
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {canApproveDueExtension && pending.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Pending AM approval</Typography>
            {pending.map((ext) => (
              <Box key={ext._id} sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip size="small" label={ext.status} color={statusColor(ext.status)} />
                  <Typography variant="body2">
                    +{ext.extensionAmount?.value} {ext.extensionAmount?.unit}
                  </Typography>
                  {ext.dgcaApprovalDocument?.fileUrl && (
                    <Button
                      size="small"
                      startIcon={<Download />}
                      href={ext.dgcaApprovalDocument.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      DGCA approval
                    </Button>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {ext.remarks}
                </Typography>
                {rejectingId === ext._id ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Rejection remarks"
                      value={rejectionRemarks}
                      onChange={(e) => setRejectionRemarks(e.target.value)}
                    />
                    <Button size="small" color="error" onClick={() => handleReject(ext._id)} disabled={loading}>
                      Confirm reject
                    </Button>
                    <Button size="small" onClick={() => setRejectingId(null)}>Cancel</Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" onClick={() => handleApprove(ext._id)} disabled={loading}>
                      Approve
                    </Button>
                    <Button size="small" color="error" onClick={() => setRejectingId(ext._id)} disabled={loading}>
                      Reject
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {history.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>History</Typography>
            <List dense disablePadding>
              {history.slice(0, 5).map((ext) => (
                <ListItem key={ext._id}>
                  <ListItemText
                    primary={`${ext.status}: +${ext.extensionAmount?.value} ${ext.extensionAmount?.unit}`}
                    secondary={ext.remarks}
                  />
                  <Chip size="small" label={ext.status} color={statusColor(ext.status)} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {canRequestDueExtension && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Request new extension (before baseline due)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Extension value"
                  value={extensionValue}
                  onChange={(e) => setExtensionValue(e.target.value)}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={extensionUnit}
                    label="Unit"
                    onChange={(e) => setExtensionUnit(e.target.value)}
                    disabled={loading}
                  >
                    {EXTENSION_UNITS.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button component="label" variant="outlined" startIcon={<AttachFile />} disabled={loading}>
                    Upload DGCA approval
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      onChange={(e) => {
                        setFile(e.target.files?.[0] || null);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  {file && (
                    <Typography variant="body2" color="text.secondary">{file.name}</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Close</Button>
        {canRequestDueExtension && (
          <Button variant="contained" onClick={handleRequest} disabled={loading}>
            Submit request
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExtendDueDialog;
